import { Library, Edit3, Wand2 } from 'lucide-react';
import { Tooltip } from '@/components/ui';
import { useHapticFeedback } from '@/hooks';
import styles from './MobileSidebar.module.css';
import type { MobileSidebarProps } from './MobileSidebar.types';
import type { Mode } from '@/types';

interface ModeConfig {
  id: Mode;
  label: string;
  Icon: typeof Library;
  tooltip: string;
}

const modes: ModeConfig[] = [
  {
    id: 'library',
    label: 'Library',
    Icon: Library,
    tooltip: 'Browse & save your designs',
  },
  {
    id: 'manual',
    label: 'Manual',
    Icon: Edit3,
    tooltip: 'Full editor with layers, text & images',
  },
  {
    id: 'ai',
    label: 'AI',
    Icon: Wand2,
    tooltip: 'Generate designs from text prompts',
  },
];

export function MobileSidebar({ activeMode, onModeChange }: MobileSidebarProps) {
  const haptic = useHapticFeedback();

  const handleModeChange = (mode: Mode) => {
    haptic.medium();
    onModeChange(mode);
  };

  return (
    <nav className={styles.mobileSidebar} aria-label="Design modes">
      <ul className={styles.modeList} role="tablist">
        {modes.map(({ id, label, Icon, tooltip }) => {
          const isActive = activeMode === id;
          return (
            <li key={id}>
              <Tooltip content={tooltip} position="right" delay={600}>
                <button
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={`${id}-panel`}
                  className={[styles.modeButton, isActive ? styles.active : '']
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => handleModeChange(id)}
                >
                  <Icon className={styles.modeIcon} aria-hidden="true" />
                  <span className={styles.modeLabel}>{label}</span>
                </button>
              </Tooltip>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
